import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { TemplateGrid } from "@/components/TemplateCard";
import { Spinner, EmptyState } from "@/components/common";
import { SearchBar } from "@/components/layout/Navbar";
import { api, FORMATS, SOFTWARE } from "@/lib/api";

const SORTS = [["newest", "Newest"], ["popular", "Popular"], ["rating", "Top rated"], ["price_asc", "Price: low to high"], ["price_desc", "Price: high to low"]];

const Chip = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${active ? "border-primary bg-primary text-white" : "border-border hover:border-primary"}`}>{children}</button>
);

function Filters({ params, set, cats }) {
  const [min, setMin] = useState(params.get("min_price") || "");
  const [max, setMax] = useState(params.get("max_price") || "");
  return (
    <div className="space-y-7">
      <div>
        <p className="eyebrow mb-3">Category</p>
        <Select value={params.get("category") || "all"} onValueChange={(v) => set("category", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <p className="eyebrow mb-3">File format</p>
        <div className="flex flex-wrap gap-2">
          {FORMATS.map((f) => <Chip key={f} testid={`filter-format-${f}`} active={params.get("format") === f} onClick={() => set("format", params.get("format") === f ? "" : f)}>{f === "CANVA" ? "Canva" : f}</Chip>)}
        </div>
      </div>
      <div>
        <p className="eyebrow mb-3">Software</p>
        <div className="flex flex-wrap gap-2">
          {SOFTWARE.map((s) => <Chip key={s} testid={`filter-software-${s}`} active={params.get("software") === s} onClick={() => set("software", params.get("software") === s ? "" : s)}>{s}</Chip>)}
        </div>
      </div>
      <div>
        <p className="eyebrow mb-3">Price (₦)</p>
        <div className="flex items-center gap-2">
          <Input data-testid="filter-min-price" type="number" placeholder="Min" value={min} onChange={(e) => setMin(e.target.value)} />
          <Input data-testid="filter-max-price" type="number" placeholder="Max" value={max} onChange={(e) => setMax(e.target.value)} />
        </div>
        <Button data-testid="filter-apply-price" variant="secondary" size="sm" className="mt-2 w-full" onClick={() => set({ min_price: min, max_price: max })}>Apply price</Button>
      </div>
      <div>
        <p className="eyebrow mb-3">Rating</p>
        <div className="flex flex-wrap gap-2">
          {["4", "3"].map((r) => <Chip key={r} testid={`filter-rating-${r}`} active={params.get("min_rating") === r} onClick={() => set("min_rating", params.get("min_rating") === r ? "" : r)}>{r}★ & up</Chip>)}
        </div>
      </div>
    </div>
  );
}

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => {
    setData(null);
    api.get("/templates", { params: Object.fromEntries(params) }).then((r) => setData(r.data));
  }, [params]);

  const set = (k, v) => {
    const n = new URLSearchParams(params);
    const upd = typeof k === "object" ? k : { [k]: v };
    Object.entries(upd).forEach(([key, val]) => (val ? n.set(key, val) : n.delete(key)));
    n.delete("page");
    setParams(n);
  };
  const active = ["category", "format", "software", "min_price", "max_price", "min_rating", "q"].filter((k) => params.get(k));
  const page = Number(params.get("page") || 1);
  const catName = cats.find((c) => c.slug === params.get("category"))?.name;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <p className="eyebrow mb-2">Marketplace</p>
      <h1 className="text-3xl font-bold sm:text-4xl" data-testid="explore-title">{params.get("q") ? `Results for “${params.get("q")}”` : catName || "Explore templates"}</h1>
      <div className="mt-6 md:hidden"><SearchBar testid="explore-search-input" initial={params.get("q") || ""} /></div>
      <div className="mt-8 grid gap-10 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block"><Filters params={params} set={set} cats={cats} /></aside>
        <div>
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <Sheet>
              <SheetTrigger asChild><Button variant="outline" size="sm" className="lg:hidden" data-testid="mobile-filters-btn"><SlidersHorizontal size={15} className="mr-2" />Filters</Button></SheetTrigger>
              <SheetContent side="left" className="overflow-y-auto"><SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader><div className="mt-6"><Filters params={params} set={set} cats={cats} /></div></SheetContent>
            </Sheet>
            <span className="text-sm text-muted-foreground" data-testid="explore-result-count">{data ? `${data.total} templates` : "…"}</span>
            {active.map((k) => (
              <button key={k} onClick={() => set(k, "")} data-testid={`active-filter-${k}`} className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs">
                {k.replace("_", " ")}: {params.get(k)}<X size={12} />
              </button>
            ))}
            <div className="ml-auto w-48">
              <Select value={params.get("sort") || "newest"} onValueChange={(v) => set("sort", v)}>
                <SelectTrigger data-testid="sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SORTS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`sort-${v}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {!data ? <Spinner /> : data.items.length === 0 ? (
            <EmptyState title="No templates found" text="Try a different search or clear some filters." action={<Button variant="outline" onClick={() => setParams({})} data-testid="clear-filters-btn">Clear filters</Button>} />
          ) : (
            <>
              <TemplateGrid items={data.items} />
              {data.pages > 1 && (
                <div className="mt-10 flex items-center justify-center gap-3">
                  <Button variant="outline" disabled={page <= 1} onClick={() => { const n = new URLSearchParams(params); n.set("page", page - 1); setParams(n); }} data-testid="page-prev">Previous</Button>
                  <span className="text-sm text-muted-foreground">Page {page} of {data.pages}</span>
                  <Button variant="outline" disabled={page >= data.pages} onClick={() => { const n = new URLSearchParams(params); n.set("page", page + 1); setParams(n); }} data-testid="page-next">Next</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
